import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiDownload,
  mdiUpload,
  mdiAlertOutline,
  mdiCheckCircleOutline,
  mdiLoading,
} from '@mdi/js';
import { apiService, WorkspaceImportResult } from '../../services/apiService';
import { useToast } from '../components/Toast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { adminTheme } from '../styles/adminTheme';

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
  margin-bottom: 4px;
`;

const PageIntro = styled.p`
  font-size: 13px;
  color: ${adminTheme.colors.textSecondary};
  margin-bottom: 20px;
  max-width: 720px;
  line-height: 1.6;
`;

const Card = styled.div`
  background: ${adminTheme.colors.cardBg};
  border: 1px solid ${adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius};
  padding: 20px;
  margin-bottom: 16px;
  max-width: 720px;
`;

const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 6px;
`;

const CardText = styled.p`
  font-size: 13px;
  color: ${adminTheme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 14px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  margin-bottom: 10px;
  cursor: pointer;
`;

const CheckboxHint = styled.span`
  display: block;
  font-size: 12px;
  color: ${adminTheme.colors.textSecondary};
  margin-top: 2px;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${adminTheme.colors.primary};
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${adminTheme.colors.primaryHover};
  }

  &:disabled {
    background: ${adminTheme.colors.textMuted};
    cursor: default;
  }
`;

const DangerButton = styled(Button)`
  background: ${adminTheme.colors.danger};

  &:hover:not(:disabled) {
    background: ${adminTheme.colors.dangerHover};
  }
`;

const Warning = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  background: #fef7e0;
  border: 1px solid #f9dfa3;
  border-radius: 6px;
  font-size: 12px;
  color: #7a4f01;
  line-height: 1.5;
  margin-bottom: 14px;
`;

const FileName = styled.span`
  font-size: 13px;
  color: ${adminTheme.colors.textSecondary};
  margin-left: 10px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const Summary = styled.div`
  margin-top: 16px;
  border-top: 1px solid ${adminTheme.colors.border};
  padding-top: 14px;
`;

const SummaryTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 8px;
`;

const CountRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${adminTheme.colors.textSecondary};
  padding: 3px 0;
  font-family: 'SFMono-Regular', Consolas, monospace;
`;

const Spin = styled(Icon)`
  animation: spin 1s linear infinite;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/** `elements.ComponentType` reads better as `ComponentType`. */
const shortLabel = (label: string) => label.split('.').pop() || label;

const CountList: React.FC<{ title: string; counts?: Record<string, number> }> = ({
  title, counts,
}) => {
  const entries = Object.entries(counts || {}).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  return (
    <>
      <CountRow style={{ fontWeight: 600, marginTop: 6 }}>
        <span>{title}</span>
        <span />
      </CountRow>
      {entries.map(([label, count]) => (
        <CountRow key={label}>
          <span>{shortLabel(label)}</span>
          <span>{count}</span>
        </CountRow>
      ))}
    </>
  );
};

export const WorkspacePage: React.FC = () => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [exportUsers, setExportUsers] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [importUsers, setImportUsers] = useState(false);
  const [replace, setReplace] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<WorkspaceImportResult | null>(null);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await apiService.downloadWorkspaceExport(exportUsers);
      toast.success('Export downloaded.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }, [exportUsers, toast]);

  const runImport = useCallback(async (dryRun: boolean) => {
    if (!file) return;
    setImporting(true);
    try {
      const outcome = await apiService.importWorkspace(file, {
        includeUserData: importUsers,
        replace,
        dryRun,
      });
      setResult(outcome);
      toast.success(
        dryRun
          ? 'Archive inspected — nothing was written.'
          : `Imported ${outcome.record_count} records.`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
    }
  }, [file, importUsers, replace, toast]);

  return (
    <div>
      <PageTitle>Workspace transfer</PageTitle>
      <PageIntro>
        Move a component library between environments — from a development
        instance into this one, for example. The archive is a portable format
        rather than a database dump, so it transfers between SQLite and
        PostgreSQL, and it carries uploaded images and source spreadsheets
        alongside the records.
      </PageIntro>

      <Card>
        <CardTitle>Export</CardTitle>
        <CardText>
          Downloads components, characteristic curves, coolant fluids and
          circuit templates, together with their media.
        </CardText>

        <CheckboxRow>
          <input
            type="checkbox"
            checked={exportUsers}
            onChange={e => setExportUsers(e.target.checked)}
          />
          <span>
            Include user accounts
            <CheckboxHint>
              Adds accounts, projects, saved circuits and custom components.
              The archive will contain password hashes — treat it as a secret.
            </CheckboxHint>
          </span>
        </CheckboxRow>

        <Button onClick={handleExport} disabled={exporting}>
          {exporting
            ? <Spin path={mdiLoading} size={0.7} />
            : <Icon path={mdiDownload} size={0.7} />}
          Download archive
        </Button>
      </Card>

      <Card>
        <CardTitle>Import</CardTitle>
        <CardText>
          Loads an archive into this environment. Components keep their ids so
          templates still reference them; accounts are matched by email and
          saved circuits by their share link, so importing twice updates rather
          than duplicates.
        </CardText>

        <HiddenInput
          ref={fileInputRef}
          type="file"
          accept=".zip"
          onChange={e => {
            setFile(e.target.files?.[0] || null);
            setResult(null);
          }}
        />
        <div style={{ marginBottom: 14 }}>
          <Button onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <Icon path={mdiUpload} size={0.7} />
            Choose archive
          </Button>
          {file && <FileName>{file.name}</FileName>}
        </div>

        <CheckboxRow>
          <input
            type="checkbox"
            checked={importUsers}
            onChange={e => setImportUsers(e.target.checked)}
          />
          <span>
            Include user accounts
            <CheckboxHint>
              Without this, any accounts in the archive are skipped and the
              accounts here are left alone.
            </CheckboxHint>
          </span>
        </CheckboxRow>

        <CheckboxRow>
          <input
            type="checkbox"
            checked={replace}
            onChange={e => setReplace(e.target.checked)}
          />
          <span>
            Replace instead of merge
            <CheckboxHint>
              Clears the library here first so this environment ends up
              mirroring the archive. Superuser accounts are never deleted.
            </CheckboxHint>
          </span>
        </CheckboxRow>

        {replace && (
          <Warning>
            <Icon path={mdiAlertOutline} size={0.75} />
            <span>
              Replacing deletes the current library
              {importUsers && ', and every non-superuser account with everything they own'}
              . This cannot be undone — export a copy first if you may want it back.
            </span>
          </Warning>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            onClick={() => runImport(true)}
            disabled={!file || importing}
          >
            {importing ? <Spin path={mdiLoading} size={0.7} /> : null}
            Inspect archive
          </Button>
          {replace ? (
            <DangerButton
              onClick={() => setConfirmOpen(true)}
              disabled={!file || importing}
            >
              Replace and import
            </DangerButton>
          ) : (
            <Button
              onClick={() => runImport(false)}
              disabled={!file || importing}
            >
              Import
            </Button>
          )}
        </div>

        {result && (
          <Summary>
            <SummaryTitle>
              <Icon path={mdiCheckCircleOutline} size={0.7} />
              {result.dry_run ? 'Archive contents' : 'Import complete'}
            </SummaryTitle>
            <CountRow>
              <span>exported</span>
              <span>{new Date(result.manifest.exported_at).toLocaleString()}</span>
            </CountRow>
            <CountRow>
              <span>scope</span>
              <span>{result.manifest.scope.join(', ')}</span>
            </CountRow>
            {result.dry_run ? (
              <CountList title="in archive" counts={result.manifest.counts} />
            ) : (
              <>
                <CountList title="deleted" counts={result.deleted} />
                <CountList title="loaded" counts={result.loaded} />
                <CountList title="skipped" counts={result.skipped} />
                <CountRow style={{ fontWeight: 600, marginTop: 6 }}>
                  <span>media files restored</span>
                  <span>{result.media_restored}</span>
                </CountRow>
              </>
            )}
          </Summary>
        )}
      </Card>

      {confirmOpen && (
        <ConfirmDialog
          title="Replace this environment's data?"
          message={
            importUsers
              ? 'The current library and every non-superuser account will be deleted, then replaced with the archive. Superusers are kept.'
              : 'The current component library will be deleted, then replaced with the archive.'
          }
          confirmLabel="Replace and import"
          danger
          onConfirm={() => { setConfirmOpen(false); runImport(false); }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
};
